export const MARINE_FIXED_STEP = 1 / 120;
const MARINE_LASH_DURATION = 0.5;
const MARINE_LASH_PULSE_DURATION = 0.3;
const MARINE_LASH_PROPAGATION_DELAY = 0.06;
const MARINE_LASH_FORCE = 220;

export type MarineCharacterPhysics = "jellyfish" | "starfish" | "fish" | "turtle";

export interface MarineVec2 {
  x: number;
  y: number;
}

export interface MarinePhysicsPose extends MarineVec2 {
  angle: number;
}

export interface MarineTentaclePoint extends MarineVec2 {
  px: number;
  py: number;
}

export interface MarineTentacleChain {
  anchor: MarineVec2;
  length: number;
  points: MarineTentaclePoint[];
}

export interface MarinePhysicsConfig {
  character: MarineCharacterPhysics;
  anchors: MarineVec2[];
  pivot: MarineVec2;
  angleOffset: number;
  segmentLength: number;
  segmentCount: number;
  waveAmplitude: number;
  dragForce: number;
  waterDrag: number;
  buoyancy: number;
}

export interface MarineLashState {
  active: boolean;
  elapsed: number;
  direction: MarineVec2;
  strength: number;
  duration: number;
}

export interface MarinePhysicsModel {
  time: number;
  angle: number;
  accumulator: number;
  config: MarinePhysicsConfig;
  lash: MarineLashState;
  chains: MarineTentacleChain[];
  fishRig?: FishPhysicsRig;
  turtleRig?: TurtlePhysicsRig;
}
export interface FishPhysicsJoint {
  angle: number;
  omega: number;
}

export interface FishPhysicsFin extends FishPhysicsJoint {
  side: -1 | 1;
  tip: FishPhysicsJoint;
}

export interface FishPhysicsRig {
  time: number;
  phase: number;
  previousSpeed: number;
  previousHeading: number;
  facing: FishFacing;
  joints: FishPhysicsJoint[];
  rays: FishPhysicsJoint[];
  fins: FishPhysicsFin[];
  whiskerModel?: MarinePhysicsModel;
}

export type FishFacing = -1 | 1;

export const FISH_FACING_THRESHOLD = 0.75;

export function resolveFishFacing(current: FishFacing, velocityX: number, threshold = FISH_FACING_THRESHOLD): FishFacing {
  if (!Number.isFinite(velocityX) || Math.abs(velocityX) <= threshold) return current;
  return velocityX > 0 ? 1 : -1;
}

export function fishOrientationDegrees(heading: number, facing: FishFacing): number {
  if (!Number.isFinite(heading)) return 0;
  const lateralOffset = facing === 1 ? 0 : Math.PI;
  return wrapFishAngle(heading - lateralOffset) * 180 / Math.PI;
}

export interface FishPhysicsGeometry {
  points: MarineVec2[];
  tips: MarineVec2[];
  fins: Array<{ root: MarineVec2; mid: MarineVec2; tip: MarineVec2; angle: number }>;
}

export interface FishPhysicsMotion {
  heading: number;
  velocityRelativeToWater: MarineVec2;
  facingVelocityX?: number;
  dt: number;
  reducedMotion: boolean;
}
export interface TurtlePhysicsFlipper extends FishPhysicsJoint {
  kind: "front" | "rear";
  side: -1 | 1;
  tip: FishPhysicsJoint;
}

export interface TurtlePhysicsTail {
  angle: number;
  omega: number;
  tip: FishPhysicsJoint;
}

export interface TurtlePhysicsRig {
  time: number;
  phase: number;
  previousSpeed: number;
  previousHeading: number;
  flippers: TurtlePhysicsFlipper[];
  tail: TurtlePhysicsTail;
}

export interface TurtlePhysicsMotion {
  heading: number;
  velocityRelativeToWater: MarineVec2;
  angularVelocity: number;
  longitudinalAcceleration: number;
  dt: number;
  reducedMotion: boolean;
}

export interface TurtlePhysicsGeometryFlipper {
  root: MarineVec2;
  mid: MarineVec2;
  tip: MarineVec2;
  angle: number;
  kind: "front" | "rear";
  side: -1 | 1;
}

export interface TurtlePhysicsGeometry {
  flippers: TurtlePhysicsGeometryFlipper[];
  tail: { root: MarineVec2; mid: MarineVec2; tip: MarineVec2; angle: number };
}

const CHARACTER_CONFIGS: Record<MarineCharacterPhysics, Omit<MarinePhysicsConfig, "character">> = {
  jellyfish: {
    anchors: [-24, -16, -8, 0, 8, 16, 24].map((x) => ({ x, y: 45 })),
    pivot: { x: 0, y: 45 },
    angleOffset: Math.PI / 2,
    segmentLength: 6.4,
    segmentCount: 13,
    waveAmplitude: 8,
    dragForce: 16,
    waterDrag: 0.64,
    buoyancy: 12,
  },
  fish: {
    anchors: [-14, 0, 14].map((x) => ({ x, y: 33 })),
    pivot: { x: 0, y: 33 },
    angleOffset: 0,
    segmentLength: 3.8,
    segmentCount: 7,
    waveAmplitude: 3.5,
    dragForce: 8,
    waterDrag: 0.64,
    buoyancy: 4,
  },
  starfish: {
    anchors: [-16, -8, 0, 8, 16].map((x) => ({ x, y: 35 })),
    pivot: { x: 0, y: 35 },
    angleOffset: Math.PI / 2,
    segmentLength: 2.8,
    segmentCount: 6,
    waveAmplitude: 2.5,
    dragForce: 6,
    waterDrag: 0.64,
    buoyancy: 3,
  },
  turtle: {
    anchors: [-26, -13, 13, 26].map((x) => ({ x, y: 43 })),
    pivot: { x: 0, y: 40 },
    angleOffset: 0,
    segmentLength: 2.4,
    segmentCount: 4,
    waveAmplitude: 1.8,
    dragForce: 5,
    waterDrag: 0.64,
    buoyancy: 2.5,
  },
};
function clampFish(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function wrapFishAngle(value: number): number {
  return Math.atan2(Math.sin(value), Math.cos(value));
}

function springFishJoint(joint: FishPhysicsJoint, target: number, stiffness: number, damping: number, dt: number): void {
  joint.omega += (wrapFishAngle(target - joint.angle) * stiffness - joint.omega * damping) * dt;
  joint.angle = wrapFishAngle(joint.angle + joint.omega * dt);
}

function boundFishJoint(joint: FishPhysicsJoint, center: number, limit: number): void {
  const delta = wrapFishAngle(joint.angle - center);
  if (Math.abs(delta) > limit) {
    joint.angle = wrapFishAngle(center + Math.sign(delta) * limit);
    joint.omega *= 0.35;
  }
}

export function makeFishPhysicsRig(withWhiskers = false): FishPhysicsRig {
  return {
    time: 0,
    phase: 0,
    previousSpeed: 0,
    previousHeading: 0,
    facing: 1,
    joints: Array.from({ length: 3 }, () => ({ angle: Math.PI, omega: 0 })),
    rays: Array.from({ length: 7 }, (_, index) => ({ angle: Math.PI + (index - 3) * 0.19, omega: 0 })),
    fins: [-1, 1].map((side) => ({
      side: side as -1 | 1,
      angle: Math.PI + side * 0.95,
      omega: 0,
      tip: { angle: Math.PI + side * 0.95, omega: 0 },
    })),
    whiskerModel: withWhiskers ? makeMarinePhysicsModel("jellyfish") : undefined,
  };
}

export function updateFishPhysicsRig(rig: FishPhysicsRig, motion: FishPhysicsMotion): void {
  if (!Number.isFinite(motion.heading) || !Number.isFinite(motion.velocityRelativeToWater.x) || !Number.isFinite(motion.velocityRelativeToWater.y) || !Number.isFinite(motion.dt) || motion.dt <= 0) return;
  const oldSpeed = rig.previousSpeed;
  const oldHeading = rig.previousHeading;
  const speed = Math.hypot(motion.velocityRelativeToWater.x, motion.velocityRelativeToWater.y);
  const ratio = clampFish(speed / 105, 0, 1.7);
  rig.facing = resolveFishFacing(rig.facing, motion.facingVelocityX ?? motion.velocityRelativeToWater.x);
  rig.time += motion.dt;
  rig.previousSpeed = speed;
  rig.previousHeading = motion.heading;
  rig.phase += Math.PI * 2 * (0.3 + 1.6 * ratio) * motion.dt;

  const amplitude = motion.reducedMotion ? 0 : 0.025 + 0.22 * ratio;
  const back = motion.heading + Math.PI;
  rig.joints.forEach((joint, index) => {
    const target = back + Math.sin(rig.phase - index * 0.6) * amplitude * (0.6 + index * 0.35);
    springFishJoint(joint, target, 150 - index * 30, 18 - index * 2, motion.dt);
    boundFishJoint(joint, back, 0.8);
  });

  const base = rig.joints[2].angle;
  rig.rays.forEach((ray, index) => {
    const spread = (index - 3) * 0.19;
    const target = base + spread + (motion.reducedMotion ? 0 : Math.sin(rig.phase - 2.1 - Math.abs(index - 3) * 0.12) * amplitude * 0.38);
    springFishJoint(ray, target, 65 - Math.abs(index - 3) * 7, 11, motion.dt);
    boundFishJoint(ray, base + spread, 0.5);
  });

  const braking = clampFish((oldSpeed - speed) / motion.dt / 200, 0, 1);
  const turnRate = wrapFishAngle(motion.heading - oldHeading) / motion.dt;
  rig.fins.forEach((fin) => {
    const opening = clampFish(0.48 + 0.5 * (1 - clampFish(ratio, 0, 1)) + 0.4 * braking + turnRate * fin.side * 0.11, 0.3, 1.4);
    const stroke = motion.reducedMotion ? 0 : Math.sin(rig.phase * 0.8 + fin.side * 0.3) * (0.05 + 0.08 * clampFish(ratio, 0, 1));
    springFishJoint(fin, back + fin.side * (opening + stroke), 100, 16, motion.dt);
    boundFishJoint(fin, back + fin.side * 0.85, 0.85);
    springFishJoint(fin.tip, fin.angle + fin.side * (motion.reducedMotion ? 0 : Math.sin(rig.phase * 0.8 - 0.7) * 0.08), 45, 10, motion.dt);
    boundFishJoint(fin.tip, fin.angle, 0.55);
  });
  if (rig.whiskerModel) {
    const cosine = Math.cos(motion.heading);
    const sine = Math.sin(motion.heading);
    const localVelocity = {
      x: motion.velocityRelativeToWater.x * cosine + motion.velocityRelativeToWater.y * sine,
      y: -motion.velocityRelativeToWater.x * sine + motion.velocityRelativeToWater.y * cosine,
    };
    stepMarinePhysics(rig.whiskerModel, { x: 0, y: 0, angle: 0 }, localVelocity, motion.dt, { reducedMotion: motion.reducedMotion });
  }
}

export function makeTurtlePhysicsRig(): TurtlePhysicsRig {
  const flippers: TurtlePhysicsFlipper[] = [];
  for (const kind of ["rear", "front"] as const) {
    for (const side of [-1, 1] as const) {
      const angle = kind === "front" ? side * 1.25 : Math.PI + side * 0.65;
      flippers.push({ kind, side, angle, omega: 0, tip: { angle, omega: 0 } });
    }
  }
  return {
    time: 0,
    phase: 0,
    previousSpeed: 0,
    previousHeading: 0,
    flippers,
    tail: { angle: Math.PI, omega: 0, tip: { angle: Math.PI, omega: 0 } },
  };
}

export function updateTurtlePhysicsRig(rig: TurtlePhysicsRig, motion: TurtlePhysicsMotion): void {
  if (!Number.isFinite(motion.heading) || !Number.isFinite(motion.velocityRelativeToWater.x) || !Number.isFinite(motion.velocityRelativeToWater.y) || !Number.isFinite(motion.angularVelocity) || !Number.isFinite(motion.longitudinalAcceleration) || !Number.isFinite(motion.dt) || motion.dt <= 0) return;
  const speed = Math.hypot(motion.velocityRelativeToWater.x, motion.velocityRelativeToWater.y);
  const ratio = clampFish(speed / 88, 0, 1.6);
  rig.time += motion.dt;
  rig.previousSpeed = speed;
  rig.previousHeading = motion.heading;
  rig.phase += Math.PI * 2 * (0.15 + 0.75 * ratio) * motion.dt;

  const wave = Math.sin(rig.phase);
  const stroke = wave > 0 ? Math.sin(wave * Math.PI / 2) : wave * 0.4;
  const braking = clampFish(-motion.longitudinalAcceleration / 160, 0, 1);
  rig.flippers.forEach((flipper) => {
    if (flipper.kind === "front") {
      const sweep = motion.reducedMotion ? 0 : stroke * (0.025 + 0.68 * clampFish(ratio, 0, 1.6));
      const opening = clampFish(1.25 + sweep - motion.angularVelocity * flipper.side * 0.08 + braking * 0.12, 0.48, 2.3);
      springFishJoint(flipper, motion.heading + flipper.side * opening, 100, 14, motion.dt);
      boundFishJoint(flipper, motion.heading + flipper.side * 1.3, 1.05);
      const flex = motion.reducedMotion ? 0 : Math.sin(rig.phase - 0.75) * (0.015 + 0.20 * ratio);
      springFishJoint(flipper.tip, flipper.angle + flipper.side * (0.14 + flex), 35, 8.5, motion.dt);
      boundFishJoint(flipper.tip, flipper.angle, 0.6);
      return;
    }
    const rearStroke = motion.reducedMotion ? 0 : Math.sin(rig.phase - 0.95) * (0.015 + 0.34 * clampFish(ratio, 0, 1.6));
    const opening = clampFish(0.72 + braking * 0.35 + motion.angularVelocity * flipper.side * 0.32 + rearStroke, 0.25, 1.45);
    springFishJoint(flipper, motion.heading + Math.PI + flipper.side * opening, 105, 14, motion.dt);
    boundFishJoint(flipper, motion.heading + Math.PI + flipper.side * 0.8, 0.8);
    const rearFlex = motion.reducedMotion ? 0 : Math.sin(rig.phase - 1.5) * 0.12 * clampFish(ratio, 0, 1.6);
    springFishJoint(flipper.tip, flipper.angle + flipper.side * (0.12 + rearFlex), 50, 10, motion.dt);
    boundFishJoint(flipper.tip, flipper.angle, 0.5);
  });

  const tailWave = motion.reducedMotion ? 0 : Math.sin(rig.phase - 0.9) * (0.015 + 0.23 * ratio);
  springFishJoint(rig.tail, motion.heading + Math.PI + tailWave * 0.65, 70, 12, motion.dt);
  boundFishJoint(rig.tail, motion.heading + Math.PI, 0.6);
  const tipWave = motion.reducedMotion ? 0 : Math.sin(rig.phase - 1.5) * (0.01 + 0.18 * ratio);
  springFishJoint(rig.tail.tip, rig.tail.angle + tipWave, 36, 8, motion.dt);
  boundFishJoint(rig.tail.tip, rig.tail.angle, 0.45);
}

function rotateTurtlePoint(x: number, y: number, heading: number): MarineVec2 {
  const cosine = Math.cos(heading);
  const sine = Math.sin(heading);
  return { x: x * cosine - y * sine, y: x * sine + y * cosine };
}

export function turtlePhysicsGeometry(rig: TurtlePhysicsRig, heading: number): TurtlePhysicsGeometry {
  const flippers = rig.flippers.map((flipper) => {
    const front = flipper.kind === "front";
    const localRoot = { x: front ? 18 : -24, y: flipper.side * (front ? 23 : 21) };
    const root = rotateTurtlePoint(localRoot.x, localRoot.y, heading);
    const firstLength = front ? 25 : 16;
    const secondLength = front ? 31 : 20;
    const mid = { x: root.x + firstLength * Math.cos(flipper.angle), y: root.y + firstLength * Math.sin(flipper.angle) };
    const tip = { x: mid.x + secondLength * Math.cos(flipper.tip.angle), y: mid.y + secondLength * Math.sin(flipper.tip.angle) };
    return { root, mid, tip, angle: flipper.angle, kind: flipper.kind, side: flipper.side };
  });
  const tailRoot = rotateTurtlePoint(-34, 0, heading);
  const tailMid = { x: tailRoot.x + 10 * Math.cos(rig.tail.angle), y: tailRoot.y + 10 * Math.sin(rig.tail.angle) };
  const tailTip = { x: tailMid.x + 12 * Math.cos(rig.tail.tip.angle), y: tailMid.y + 12 * Math.sin(rig.tail.tip.angle) };
  return { flippers, tail: { root: tailRoot, mid: tailMid, tip: tailTip, angle: rig.tail.angle } };
}

export function fishPhysicsGeometry(rig: FishPhysicsRig, heading: number): FishPhysicsGeometry {
  const points: MarineVec2[] = [{ x: -29 * Math.cos(heading), y: -29 * Math.sin(heading) }];
  rig.joints.forEach((joint) => {
    const previous = points[points.length - 1];
    points.push({ x: previous.x + 8 * Math.cos(joint.angle), y: previous.y + 8 * Math.sin(joint.angle) });
  });
  const base = points[points.length - 1];
  const tips = rig.rays.map((ray, index) => {
    const length = 21 + Math.abs(index - 3) * 6;
    return { x: base.x + Math.cos(ray.angle) * length, y: base.y + Math.sin(ray.angle) * length };
  });
  const co = Math.cos(heading);
  const si = Math.sin(heading);
  const fins = rig.fins.map((fin) => {
    const root = { x: -3 * co - fin.side * 9 * si, y: -3 * si + fin.side * 9 * co };
    const mid = { x: root.x + 14 * Math.cos(fin.angle), y: root.y + 14 * Math.sin(fin.angle) };
    const tip = { x: mid.x + 14 * Math.cos(fin.tip.angle), y: mid.y + 14 * Math.sin(fin.tip.angle) };
    return { root, mid, tip, angle: fin.angle };
  });
  return { points, tips, fins };
}


function copyPoint(point: MarineVec2): MarineTentaclePoint {
  return { x: point.x, y: point.y, px: point.x, py: point.y };
}

export function makeMarinePhysicsModel(character: MarineCharacterPhysics): MarinePhysicsModel {
  const base = CHARACTER_CONFIGS[character];
  const config: MarinePhysicsConfig = { ...base, character, anchors: base.anchors.map((anchor) => ({ ...anchor })) };
  return {
    time: 0,
    angle: 0,
    accumulator: 0,
    config,
    chains: config.anchors.map((anchor) => ({
      anchor: { ...anchor },
      length: config.segmentLength,
      points: Array.from({ length: config.segmentCount }, (_, index) => copyPoint({ x: anchor.x, y: anchor.y + index * config.segmentLength })),
    })),
    lash: { active: false, elapsed: 0, direction: { x: 0, y: 0 }, strength: 0, duration: MARINE_LASH_DURATION },
    fishRig: character === "fish" ? makeFishPhysicsRig() : undefined,
    turtleRig: character === "turtle" ? makeTurtlePhysicsRig() : undefined,
  };
}

export function triggerMarineLash(model: MarinePhysicsModel, direction: MarineVec2, strength = 1): void {
  const magnitude = Math.hypot(direction.x, direction.y);
  const safeDirection = magnitude > 1e-6 ? { x: direction.x / magnitude, y: direction.y / magnitude } : { x: 1, y: 0 };
  const safeStrength = Math.max(0, strength);
  model.lash = { active: true, elapsed: 0, direction: safeDirection, strength: safeStrength, duration: MARINE_LASH_DURATION };
}

function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function shortestAngleDelta(target: number, current: number): number {
  return Math.atan2(Math.sin(target - current), Math.cos(target - current));
}

export function resolveMarineAngle(current: number, velocity: MarineVec2, dt: number, angleOffset = Math.PI / 2): number {
  const speed = Math.hypot(velocity.x, velocity.y);
  if (speed <= 5) return current;
  const target = Math.atan2(velocity.y, velocity.x) + angleOffset;
  const delta = shortestAngleDelta(target, current);
  const follow = 1 - Math.exp(-5 * dt);
  return current + Math.max(-2.8 * dt, Math.min(2.8 * dt, delta * follow));
}

export function advanceMarinePhysics(
  model: MarinePhysicsModel,
  pose: MarinePhysicsPose,
  velocity: MarineVec2,
  dt: number,
  waterVelocityAt: (x: number, y: number) => MarineVec2 = () => ({ x: 0, y: 0 }),
  reducedMotion = false,
): void {
  if (!Number.isFinite(dt) || dt <= 0) return;
  const safeDt = Math.min(dt, 0.06);
  model.time += safeDt;
  if (model.lash.active) {
    model.lash.elapsed = Math.min(model.lash.duration, model.lash.elapsed + safeDt);
    if (model.lash.elapsed >= model.lash.duration) model.lash.active = false;
  }
  model.angle = finiteOr(pose.angle, model.angle);
  const co = Math.cos(model.angle);
  const si = Math.sin(model.angle);
  const damping = Math.exp(-2.5 * safeDt);
  const water = waterVelocityAt(pose.x, pose.y);
  const relativeVelocity = { x: velocity.x - water.x, y: velocity.y - water.y };
  const speed = Math.hypot(relativeVelocity.x, relativeVelocity.y);
  if (model.fishRig) {
    updateFishPhysicsRig(model.fishRig, { heading: model.angle, velocityRelativeToWater: relativeVelocity, facingVelocityX: velocity.x, dt: safeDt, reducedMotion });
  }
  if (model.turtleRig) {
    updateTurtlePhysicsRig(model.turtleRig, {
      heading: model.angle,
      velocityRelativeToWater: relativeVelocity,
      angularVelocity: wrapFishAngle(model.angle - model.turtleRig.previousHeading) / safeDt,
      longitudinalAcceleration: (speed - model.turtleRig.previousSpeed) / safeDt,
      dt: safeDt,
      reducedMotion,
    });
  }
  const { config } = model;
  const lashElapsed = model.lash.elapsed;
  const lashDirection = model.lash.direction;

  model.chains.forEach((chain, chainIndex) => {
    const points = chain.points;
    const anchorX = pose.x + config.pivot.x + (chain.anchor.x - config.pivot.x) * co - (chain.anchor.y - config.pivot.y) * si;
    const anchorY = pose.y + config.pivot.y + (chain.anchor.x - config.pivot.x) * si + (chain.anchor.y - config.pivot.y) * co;
    points[0].x = anchorX;
    points[0].y = anchorY;
    for (let index = 1; index < points.length; index += 1) {
      const point = points[index];
      const oldX = point.x;
      const oldY = point.y;
      const wave = reducedMotion ? 0 : Math.sin(model.time * 1.7 - index * 0.55 + chainIndex * 0.75) * config.waveAmplitude;
      const ease = 1 - Math.min(speed / 100, 1);
      const dragX = -relativeVelocity.x * config.waterDrag;
      const dragY = -relativeVelocity.y * config.waterDrag;
      const dragMagnitude = Math.hypot(dragX, dragY);
      const dragLimit = config.dragForce * 10;
      const dragScale = dragMagnitude > dragLimit ? dragLimit / dragMagnitude : 1;
      const leverage = index / (points.length - 1);
      const tailWeight = Math.pow(leverage, 1.35);
      const pulseElapsed = lashElapsed - leverage * MARINE_LASH_PROPAGATION_DELAY;
      const pulseProgress = Math.max(0, Math.min(1, pulseElapsed / MARINE_LASH_PULSE_DURATION));
      const lashPulse = Math.sin(Math.PI * pulseProgress * 2);
      const lashForce = lashPulse * model.lash.strength * MARINE_LASH_FORCE * tailWeight;
      const forceX = dragX * dragScale + co * wave + lashDirection.x * lashForce;
      const forceY = dragY * dragScale + si * wave + lashDirection.y * lashForce + config.buoyancy * ease;
      point.x += (point.x - point.px) * damping + forceX * safeDt * safeDt;
      point.y += (point.y - point.py) * damping + forceY * safeDt * safeDt;
      point.px = oldX;
      point.py = oldY;
    }
    for (let pass = 0; pass < 10; pass += 1) {
      points[0].x = anchorX;
      points[0].y = anchorY;
      for (let index = 1; index < points.length; index += 1) {
        const previous = points[index - 1];
        const point = points[index];
        const dx = point.x - previous.x;
        const dy = point.y - previous.y;
        const distance = Math.hypot(dx, dy) || 1;
        const correction = (distance - chain.length) / distance;
        if (index === 1) {
          point.x -= dx * correction;
          point.y -= dy * correction;
        } else {
          previous.x += dx * correction * 0.5;
          previous.y += dy * correction * 0.5;
          point.x -= dx * correction * 0.5;
          point.y -= dy * correction * 0.5;
        }
      }
    }
    points[0].x = anchorX;
    points[0].y = anchorY;
  });
}

export function stepMarinePhysics(
  model: MarinePhysicsModel,
  pose: MarinePhysicsPose,
  velocity: MarineVec2,
  elapsedSeconds: number,
  options: { paused?: boolean; reducedMotion?: boolean; waterVelocityAt?: (x: number, y: number) => MarineVec2 } = {},
): number {
  if (options.paused) {
    model.accumulator = 0;
    return 0;
  }
  if (![pose.x, pose.y, pose.angle, velocity.x, velocity.y, elapsedSeconds].every(Number.isFinite) || elapsedSeconds < 0) {
    throw new RangeError("Marine physics inputs must be finite and elapsedSeconds nonnegative");
  }
  model.accumulator += Math.min(elapsedSeconds, 0.06);
  let steps = 0;
  while (model.accumulator + 1e-12 >= MARINE_FIXED_STEP) {
    advanceMarinePhysics(model, pose, velocity, MARINE_FIXED_STEP, options.waterVelocityAt, options.reducedMotion);
    model.accumulator = Math.max(0, model.accumulator - MARINE_FIXED_STEP);
    steps += 1;
  }
  return steps;
}

export function marineTentaclePath(chain: MarineTentacleChain, offset: MarineVec2 = { x: 0, y: 0 }): string {
  const points = chain.points.map((point) => ({ x: point.x - offset.x, y: point.y - offset.y }));
  if (!points.length) return "";
  let path = `M ${points[0].x} ${points[0].y}`;
  for (let index = 1; index < points.length - 1; index += 1) {
    const point = points[index];
    const next = points[index + 1];
    path += ` Q ${point.x} ${point.y} ${(point.x + next.x) / 2} ${(point.y + next.y) / 2}`;
  }
  if (points.length > 1) {
    const penultimate = points[points.length - 2];
    const last = points[points.length - 1];
    path += ` Q ${penultimate.x} ${penultimate.y} ${last.x} ${last.y}`;
  }
  return path;
}
